import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { Link } from 'react-router-dom';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { JournalEntrySummary } from '@/api/models';
import {
    JOURNAL_COLUMN,
    sourceLabelKey,
    documentLink,
    documentRefLabel,
    entryNumberDisplay,
    formatEntryAmount,
    formatEntryDate,
} from '../../journalMeta';
import { JournalStatusChip } from '../journalStatusChip';

type JournalTableProps = {
    entries: JournalEntrySummary[];
    onSelect: (id: string) => void;
    /** The window these entries were read over, already display-formatted. */
    fromDisplay: string;
    toDisplay: string;
    /** Widen the shell's window; absent outside the Books shell (BOS-166). */
    onWiden?: (() => void) | undefined;
};

/**
 * Journal list table (HeroUI Pro DataGrid). The whole row is the click target
 * (`onRowAction`) — activating a row opens the detail drawer.
 */
export const JournalTable: React.FC<JournalTableProps> = ({
    entries,
    onSelect,
    fromDisplay,
    toDisplay,
    onWiden,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<JournalEntrySummary>[]>(
        () => [
            {
                id: JOURNAL_COLUMN.date,
                header: t('journal.columns.date'),
                isRowHeader: true,
                width: 128,
                cell: (entry) => (
                    <span className="text-sm tabular-nums text-muted">
                        {formatEntryDate(entry.date)}
                    </span>
                ),
            },
            {
                id: JOURNAL_COLUMN.entryNumber,
                header: t('journal.columns.entryNumber'),
                width: 148,
                cell: (entry) => (
                    <span className="flex items-center gap-1.5">
                        {/* A draft has no number yet — numbering is what
                            posting does, and it is gapless, so it cannot be
                            handed out in advance. The dash was the only thing
                            saying so, and a dash reads as missing data.
                            `title` + sr-only text says which it is (BOS-170). */}
                        <span
                            className="text-sm tabular-nums text-foreground"
                            title={
                                entry.entryNumber
                                    ? undefined
                                    : t('journal.unnumberedDraft')
                            }
                        >
                            {entryNumberDisplay(
                                entry,
                                t('journal.pendingNumber'),
                            )}
                            {entry.entryNumber ? null : (
                                <span className="sr-only">
                                    {` ${t('journal.unnumberedDraft')}`}
                                </span>
                            )}
                        </span>
                        {entry.pendingSync ? (
                            <Icon
                                aria-label={t('journal.pendingSync')}
                                className="size-3.5 text-warning-soft-foreground"
                                icon="gravity-ui:clock-arrow-rotate-left"
                            />
                        ) : null}
                    </span>
                ),
            },
            {
                id: JOURNAL_COLUMN.description,
                header: t('journal.columns.description'),
                minWidth: 220,
                cell: (entry) => (
                    <span className="truncate text-sm text-foreground">
                        {entry.description || '—'}
                    </span>
                ),
            },
            {
                id: JOURNAL_COLUMN.document,
                header: t('journal.columns.document'),
                width: 168,
                cell: (entry) => {
                    // A sale's ref is its transaction id and a purchase's is
                    // the invoice number, so those two can take the reader to
                    // the document the figures came from. Everything else's ref
                    // is free text with nothing behind it.
                    const href = documentLink(entry.source, entry.documentRef);
                    const label = documentRefLabel(
                        entry.source,
                        entry.documentRef,
                    );
                    if (!href) {
                        return (
                            <span className="text-sm tabular-nums text-muted">
                                {label ?? '–'}
                            </span>
                        );
                    }
                    return (
                        <Link
                            className="truncate text-sm tabular-nums text-accent underline-offset-2 hover:underline"
                            // The row itself opens the entry drawer; without
                            // this the link would do both at once.
                            onClick={(event) => event.stopPropagation()}
                            to={href}
                            {...getNodeTestIdProp(
                                `journal-row-document-link-${entry.id}`,
                            )}
                        >
                            {label}
                        </Link>
                    );
                },
            },
            {
                id: JOURNAL_COLUMN.source,
                header: t('journal.columns.source'),
                width: 128,
                cell: (entry) => (
                    <span
                        className="text-sm text-muted"
                        {...getNodeTestIdProp(`journal-row-source-${entry.id}`)}
                    >
                        {sourceLabelKey(entry.source)
                            ? t(sourceLabelKey(entry.source)!)
                            : entry.source}
                    </span>
                ),
            },
            {
                id: JOURNAL_COLUMN.amount,
                header: t('journal.columns.amount'),
                align: 'end',
                width: 148,
                cell: (entry) => (
                    <span className="text-sm font-medium tabular-nums text-foreground">
                        {formatEntryAmount(entry.amountTetri)}
                    </span>
                ),
            },
            {
                id: JOURNAL_COLUMN.status,
                header: t('journal.columns.status'),
                width: 128,
                cell: (entry) => (
                    <JournalStatusChip slot={entry.id} status={entry.status} />
                ),
            },
        ],
        [t],
    );

    // `contents` wrapper: `DataGrid` forwards no unknown props, so a spread
    // testid never reaches the DOM. `display: contents` adds no box, so the
    // grid's layout and scoped CSS are untouched.
    return (
        <div className="contents" {...getNodeTestIdProp('journal-table')}>
            <DataGrid<JournalEntrySummary>
                aria-label={t('journal.tableAria')}
                className={cn('coa-data-grid', 'ledger-data-grid')}
                columns={columns}
                contentClassName="min-w-[760px]"
                data={entries}
                getRowId={(entry) => entry.id}
                onRowAction={(key) => onSelect(String(key))}
                renderEmptyState={() => (
                    <div
                        className="flex flex-col items-center gap-3 py-12 text-muted"
                        {...getNodeTestIdProp('journal-table-empty')}
                    >
                        <Icon
                            aria-hidden
                            className="size-8 text-default-500"
                            icon="gravity-ui:folder-open"
                        />
                        {/* BOS-166: an empty journal almost always means the
                            window is narrow, not that the books are. Say which
                            window, and offer the one click that widens it. */}
                        <span className="text-sm">
                            {t('journal.emptyInWindow', {
                                from: fromDisplay,
                                to: toDisplay,
                            })}
                        </span>
                        {onWiden ? (
                            <Button
                                onPress={onWiden}
                                size="sm"
                                variant="secondary"
                                {...getNodeTestIdProp('journal-table-widen')}
                            >
                                {t('journal.widen')}
                            </Button>
                        ) : null}
                    </div>
                )}
                variant="primary"
            />
        </div>
    );
};
